import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { TelegramErrorReporterService } from 'src/app/core/services/telegram-error-reporter.service';

interface ErrorLogContext {
  route: string;
  type: string;
  stack?: string | null;
  name?: string;
  source?: string;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorLoggerService implements ErrorHandler {
  constructor(
    private injector: Injector,
    private logger: LoggerService,
    private telegramReporter: TelegramErrorReporterService
  ) {}

  // Logging improves observability, but it is not a security boundary.
  // Real protection still depends on backend validation and secure auth/session handling.
  handleError(error: unknown): void {
    const normalizedError = this.normalizeError(error);
    const route = this.getCurrentRoute();
    const context: ErrorLogContext = {
      route,
      type: 'GLOBAL_RUNTIME',
      stack: normalizedError.stack,
      name: normalizedError.name,
      timestamp: new Date().toISOString()
    };

    this.logger.error('Unhandled application error captured', {
      route,
      name: normalizedError.name,
      message: normalizedError.message
    });

    this.telegramReporter.report({
      type: 'GLOBAL_RUNTIME',
      message: normalizedError.message,
      name: normalizedError.name,
      stack: normalizedError.stack,
      route
    });

    this.logError(normalizedError.message, context);
  }

  logError(_message: string, _data: object): void {
    // Remote /error-log API disabled
  }

  private getCurrentRoute(): string {
    try {
      return this.injector.get(Router).url;
    } catch {
      return '/';
    }
  }

  private normalizeError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === 'string') {
      return new Error(error);
    }

    if (error && typeof error === 'object') {
      const errorRecord = error as Record<string, unknown>;
      const message = typeof errorRecord['message'] === 'string'
        ? errorRecord['message']
        : 'Unknown client-side error';
      const normalized = new Error(message);
      normalized.name = typeof errorRecord['name'] === 'string' ? errorRecord['name'] : 'Error';
      if (typeof errorRecord['stack'] === 'string') {
        normalized.stack = errorRecord['stack'];
      }
      return normalized;
    }

    return new Error('Unknown client-side error');
  }
}
